/**
 * SockJS auth helpers.
 *
 * STOMP connectHeaders only apply to the STOMP CONNECT frame. The initial SockJS
 * GET /ws/info request is a separate XHR call and must carry Authorization too.
 * sockjs-client does not pass transportOptions to /info, so we install a scoped
 * XHR interceptor for requests targeting the /ws path.
 */
import LocalStorageHandler from "../service/memory/handler/LocalStorageHandler";
import MemoryType from "../service/memory/enum/MemoryType";

const localStorage = new LocalStorageHandler();

let activeInterceptorCount = 0;
let originalXhrSend = null;
let originalXhrOpen = null;

const isSockJsRequest = (url) => {
  if (!url) {
    return false;
  }

  return String(url).includes("/ws");
};

export const getSocketAuthToken = () => {
  const token = localStorage.read(MemoryType.TOKEN);

  if (!token || typeof token !== "string") {
    return null;
  }

  return token.trim() || null;
};

export const buildSocketAuthHeaders = (token = getSocketAuthToken()) => {
  if (!token) {
    return {};
  }

  return {
    Authorization: token,
  };
};

export const installSockJsXhrAuthInterceptor = () => {
  if (typeof window === "undefined") {
    return;
  }

  activeInterceptorCount += 1;

  if (activeInterceptorCount > 1) {
    return;
  }

  originalXhrSend = XMLHttpRequest.prototype.send;
  originalXhrOpen = XMLHttpRequest.prototype.open;

  XMLHttpRequest.prototype.open = function openWithUrl(method, url, ...rest) {
    this.__sockjsRequestUrl = url;
    return originalXhrOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function sendWithAuth(...args) {
    const requestUrl = String(this.__sockjsRequestUrl || "");
    const authToken = getSocketAuthToken();

    if (isSockJsRequest(requestUrl) && authToken) {
      try {
        this.setRequestHeader("Authorization", authToken);
      } catch {
        // Header may already be set or the request may be in an invalid state.
      }
    }

    return originalXhrSend.apply(this, args);
  };
};

export const uninstallSockJsXhrAuthInterceptor = () => {
  if (activeInterceptorCount === 0) {
    return;
  }

  activeInterceptorCount -= 1;

  if (activeInterceptorCount > 0 || !originalXhrSend || !originalXhrOpen) {
    return;
  }

  XMLHttpRequest.prototype.send = originalXhrSend;
  XMLHttpRequest.prototype.open = originalXhrOpen;
  originalXhrSend = null;
  originalXhrOpen = null;
};

/**
 * @param {string} endpointUrl
 * @param {import('sockjs-client')} SockJS
 * @param {string} [token]
 */
export const createAuthenticatedSockJS = (endpointUrl, SockJS, token = getSocketAuthToken()) => {
  const authHeaders = buildSocketAuthHeaders(token);

  return new SockJS(endpointUrl, null, {
    transports: ["websocket", "xhr-streaming", "xhr-polling"],
    transportOptions: {
      "xhr-streaming": { headers: authHeaders },
      "xhr-polling": { headers: authHeaders },
    },
  });
};
